import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { format as formatDate, startOfDay, endOfDay } from 'date-fns';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import AwpDropdown from '@components/Common/DropDown/AwpDropdown';
import Table, { cellComponentExp } from '@awp/components/table';
import { Form, Row, Col, Card, Accordion, Container } from 'react-bootstrap';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import { GetJson } from '@awp/helpers/fetchReact';
import {
  REST_API,
  RoadStatus,
  EventTypes,
  FORM_MODE,
  Permissions,
  EnabledFeature,
  ForecastLevel,
} from '@awp/constants';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { startDateValidator, endDateValidator, dateBuilder } from '@awp/components/Common/Helpers/AwpDateHelper';
import DatePicker from '@components/Common/AwpDatePicker';
import {
  getAvalancheGeographicAreas,
  getAllGeographicAreas,
  getAvalancheGeographicPaths,
  selectGeographicAreaLinesDd,
  selectGeographicAreaPointsDd,
} from '@awp/stores/avalancheSlice';
import {
  convLocalToIsoString,
  getUtcToLocalByFormat,
  getUtcToLocalByBreakpoints,
  isValueEmpty,
  getLocationPrefix,
  resizePointLocationName,
} from '@awp/helpers/utilsReact';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import { selectEventTypes, selectPhasedEventTypes } from '@awp/stores/eventSlice';
import { selectAllHazardForecastDropDown } from '@awp/stores/hazardForecastSlice';
import { hasAccess } from '@components/Authorize';
import HazardForecastDeleteButton from './HazardForecastDeleteButton';
import HazardForecastDuplicateButton from './HazardForecastDuplicateButton';
import EventDuplicateButton from './EventDuplicateButton';
import EventDeleteButton from './EventDeleteButton';
import { useAvalancheTableFilterContext } from './AvalancheTableFilterContext';
import { useAvalancheEventContext } from './AvalanceEventContext';

const ProcessedEventTable = ({
  title,
  breakpoints,
  loading,
  setLoading,
  setMode,
  eventId,
  setEventId,
  setEventTypeToActOn,
  eventTypeToCreate,
  setEventTypeToCreate,
  sort,
  setSort,
  setIsDuplicated,
  isInitialRender,
  expandedRowEntityId,
  setExpandedRowEntityId,
  isAccordionExpanded,
  setIsAccordionExpanded,
}) => {
  const {
    startDate,
    endDate,
    selectedProgram,
    selectedArea,
    selectedStatus,
    selectedAvalanchTech,
    selectedEventType,
    search,
    scrollPosition,
    index,
    defaultPageSize,
    setEndDate,
    setStartDate,
    setSelectedProgram,
    setSelectedArea,
    setSelectedStatus,
    setSelectedAvalancheTech,
    setSelectedEventType,
    setSearch,
    setScrollPosition,
    setIndex,
    setDefaultPageSize,
  } = useAvalancheTableFilterContext();

  const { fullContextData, setFullContextData } = useAvalancheEventContext();

  const currentUser = useSelector(selectCurrentUser);
  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const allAvalancheAreas = useSelector((state) => getAllGeographicAreas(state));
  const avalancheHazardAreas = useSelector((state) => selectAllHazardForecastDropDown(state));
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const avalanchePaths = useSelector((state) => getAvalancheGeographicPaths(state));
  const areaLines = useSelector((state) => selectGeographicAreaLinesDd(state));
  const areaPoints = useSelector((state) => selectGeographicAreaPointsDd(state));
  const eventTypes = useSelector(selectEventTypes);
  const phasedEventTypes = useSelector(selectPhasedEventTypes);
  const [errors, setErrors] = useState(null);
  const [showYesNoModal, setShowYesNoModal] = useState(false);
  const processedEventStatus = {
    options: [
      { value: 'Created', label: 'Created' },
      { value: 'Edited', label: 'Edited' },
      { value: 'Pushed', label: 'Pushed' },
      { value: 'PushFailed', label: 'PushFailed' },
      { value: 'Transmitted', label: 'Transmitted' },
    ],
    status: 'succeeded',
  };

  const EntityId = 'EventId';

  const hasSearch = (src) => {
    return src ? src.toString().toLowerCase().includes(search.toLowerCase()) : false;
  };

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    applyUserPreferences();
    loadFullData();
  }, [startDate, endDate]);

  const applyUserPreferences = () => {
    const data = (JSON.parse(currentUser.Preferences) ?? {})?.list;

    if (!data) return;

    if (data.programs && isValueEmpty(selectedProgram)) {
      setSelectedProgram(avalanchePrograms.filter((x) => data.programs.includes(x.value)));
    }

    if (data.areas && isValueEmpty(selectedArea)) {
      setSelectedArea(avalancheAreas.filter((x) => data.areas.includes(x.value)));
    }

    if (data.events && isValueEmpty(selectedEventType)) {
      setSelectedEventType(eventTypes.filter((x) => data.events.includes(x.value)));
    }
  };

  const loadFullData = async () => {
    setLoading(true);
    const dateFrom = formatDate(startDate.value, 'yyyy-MM-dd');
    const dateTo = formatDate(endDate.value, 'yyyy-MM-dd');
    const url = `${REST_API.AVALANCHE}processedevents?dateFrom=${dateFrom}&dateTo=${dateTo}&eventType=${EventTypes.RoadClosure}`;

    GetJson(url)
      .then((response) => response.json())
      .then(async (records) => {
        if (records) {
          setFullContextData(records.sort((a, b) => new Date(a.EventTimestamp) - new Date(b.EventTimestamp)));
        }
        setLoading(false);
      });
  };

  const getResizedLocationName = (evt, breakpoints) => {
    let ret = getLocation(evt);
    if (evt.EventTypeNm === EventTypes.AvalancheOccurrence || evt.EventTypeNm === EventTypes.AvalancheControl) {
      if (ret) return getLocationPrefix(ret, breakpoints);
    } else if (evt.EventTypeNm === EventTypes.HazardForecast) {
      if (ret) return getLocationPrefix(ret, breakpoints);
    } else {
      if (!isValueEmpty(evt.GeographicLineId)) return getLocationPrefix(ret, breakpoints);
      if (ret) return resizePointLocationName(ret, breakpoints);
    }
    return ret;
  };
  const findObjectById = (data, id) => {
    for (let i = 0; i < data.length; i++) {
      const obj = data[i];
      if (obj.value === id) {
        return obj;
      } else if (obj.children) {
        const found = findObjectById(obj.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };
  const getLocation = (evt) => {
    const ret = '';
    if (!evt) return ret;

    if (evt.EventTypeNm === EventTypes.RoadClosure || phasedEventTypes.find((x) => x.value === evt.EventTypeNm)) {
      if (!areaLines || !areaPoints) return ret;
      let segment = null;
      let fromPoint = null;
      let toPoint = null;
      if (!isValueEmpty(evt.GeographicLineId)) {
        segment = areaLines.find((x) => x.value === evt.GeographicLineId);
      } else {
        if (isValueEmpty(evt.GeographicPointIds)) return ret;
        let points = evt.GeographicPointIds.split(',');
        if (isValueEmpty(points)) return ret;
        if (!isValueEmpty(points[0])) fromPoint = areaPoints.find((x) => x.value + '' === points[0] + '');
        if (!isValueEmpty(points[1])) toPoint = areaPoints.find((x) => x.value + '' === points[1] + '');
      }
      if (segment) return segment.label;
      if (fromPoint && toPoint) return `${fromPoint.label} / ${toPoint.label}`;
      if (fromPoint) return `${fromPoint.label} / - `;
      if (toPoint) return `- / ${toPoint.label}`;
      return ret;
    } else if (evt.EventTypeNm === EventTypes.AvalancheOccurrence || evt.EventTypeNm === EventTypes.AvalancheControl) {
      if (avalanchePaths) {
        let p = avalanchePaths.find((x) => x.areaId === evt.GeographicAreaId);
        if (p) return p.label;
      }
    } else if (evt.EventTypeNm === EventTypes.HazardForecast) {
      let hf = findObjectById(avalancheHazardAreas, evt.GeographicAreaId);
      if (hf) return hf.label;
    }

    return ret;
  };

  const getFilteredArea = () => {
    const programIds = selectedProgram?.map((x) => x.value);

    if (programIds && programIds.length !== 0) {
      return avalancheAreas
        .filter((a) => programIds.includes(a.parentId))
        .sort((a, b) => a.lastUpdated - b.lastUpdated);
    }

    return avalancheAreas;
  };

  const getProgramName = (id) => {
    if (isValueEmpty(id)) return '';
    let p = avalanchePrograms.find((x) => x.value === id);
    if (!isValueEmpty(p)) {
      return p.label;
    }
    return '';
  };

  // recursively filter event and iterate over its child events
  const filterEvent = (filtered, selectedValues, field) => {
    filtered = filtered.filter((o) => {
      let childEvts = null;
      let _appCreateUserId = (o.AppCreateUserId + '').toLowerCase().replace('idir\\', '');

      if (o.ChildAvalancheProcessedEvents && o.ChildAvalancheProcessedEvents.length > 0) {
        // recursively iterate over child events
        const filteredChildEvents = filterEvent(o.ChildAvalancheProcessedEvents, selectedValues, field);
        if (filteredChildEvents.length > 0) {
          childEvts = filteredChildEvents;
          o.ChildAvalancheProcessedEvents = [...filteredChildEvents];
        } else {
          // if none of the child events match the selected value, remove the child events
          o.ChildAvalancheProcessedEvents = null;
        }
      }
      // if the parent event matches the selected value or has child events that match the selected value, keep the parent event
      if (field === 'status') {
        return selectedValues.includes(o.Status) || (childEvts && childEvts.length > 0);
      } else if (field === 'submittedBy') {
        return (
          (selectedValues.includes(_appCreateUserId) && (o.AppCreateUserDirectory + '').toLowerCase() === 'idir') ||
          (childEvts && childEvts.length > 0)
        );
      } else if (field === 'eventType') {
        return selectedValues.includes(o.EventTypeNm) || (childEvts && childEvts.length > 0);
      } else if (field === 'search') {
        return (
          hasSearch(getProgramName(o.ParentGeographicAreaId)) ||
          hasSearch(getProgramName(o.GeographicAreaId)) ||
          hasSearch(getLocation(o)) ||
          hasSearch(o.AppCreateUserId ? _appCreateUserId : null) ||
          hasSearch(o.Status) ||
          hasSearch(o.NoteComment) ||
          (childEvts && childEvts.length > 0)
        );
      }
    });
    return filtered;
  };

  const getFilteredData = () => {
    if (isValueEmpty(fullContextData)) return fullContextData;

    const programIds = selectedProgram?.map((x) => x.value);
    const areaIds = selectedArea?.map((x) => x.value);
    const eventTypeIds = selectedEventType?.map((x) => x.value);
    const statusLabels = selectedStatus?.map((x) => x.label);
    const avTechNames = selectedAvalanchTech?.map((x) => x.name.toLowerCase());

    const _fullData = JSON.parse(JSON.stringify(fullContextData)); // need a deep clone as the child objs will be updated

    let filtered = [];
    _fullData.map((o) => {
      if (
        (getUtcToLocalByFormat(o.EventTimestamp) >=
          getUtcToLocalByFormat(convLocalToIsoString(startOfDay(startDate.value))) &&
          getUtcToLocalByFormat(o.EventTimestamp) <=
            getUtcToLocalByFormat(convLocalToIsoString(endOfDay(endDate.value)))) ||
        o.EventTimestamp === undefined ||
        o.EventTimestamp === null
      )
        filtered.push(o);
    });

    if (programIds && programIds.length !== 0) {
      filtered = filtered.filter((o) => {
        if (o) {
          const prgm = allAvalancheAreas.find((p) => p.value === o.GeographicAreaId);
          if (prgm) return programIds.includes(prgm?.programId);
        }
      });
    }

    if (areaIds && areaIds.length !== 0) {
      filtered = filtered.filter((o) => {
        return areaIds.includes(o?.EventTypeNm === 'AVALANCHE EVENT' ? o?.ParentGeographicAreaId : o?.GeographicAreaId);
      });
    }

    if (statusLabels && statusLabels.length !== 0) {
      filtered = filterEvent(filtered, statusLabels, 'status');
    }

    if (avTechNames && avTechNames.length !== 0) {
      filtered = filterEvent(filtered, avTechNames, 'submittedBy');
    }

    if (eventTypeIds && eventTypeIds.length !== 0) {
      filtered = filterEvent(filtered, eventTypeIds, 'eventType');
    }

    if (!isValueEmpty(search)) {
      filtered = filterEvent(filtered, search, 'search');
    }

    if (expandedRowEntityId) {
      filtered = filtered.map((row) => {
        if (expandedRowEntityId === row[EntityId]) {
          row.expanded = true;
        }

        return row;
      });
    }

    return filtered.sort((a, b) => {
      const eventTimestampB = new Date(b.EventTimestamp);
      const eventTimestampA = new Date(a.EventTimestamp);

      if (eventTimestampB > eventTimestampA) {
        return -1;
      } else if (eventTimestampB < eventTimestampA) {
        return 1;
      } else {
        const appCreateTimestampB = new Date(b.AppCreateTimestamp);
        const appCreateTimestampA = new Date(a.AppCreateTimestamp);

        if (appCreateTimestampB > appCreateTimestampA) {
          return -1;
        } else if (appCreateTimestampB < appCreateTimestampA) {
          return 1;
        } else {
          return 0;
        }
      }
    });
  };
  const actions = (record, eventType) => (
    <>
      <i
        className="fas fa-edit mx-1 my-1 awp-img-btn"
        title="Edit"
        onClick={() => {
          setMode(FORM_MODE.EDIT);
          setEventId(record.EventId);
          setEventTypeToActOn(eventType);
          setIsDuplicated(false);
        }}
      ></i>
    </>
  );

  const actionMenu = (o, fromDate, toDate) => {
    if (!o) return '';
    let row = o.row;
    if (!row) return '';
    let rows = o.rows;
    if (!rows) return '';
    let toggleRowExpanded = o.toggleRowExpanded;
    let isChild = o.isChild ? o.isChild : false;
    const record = o.row.original;
    if (!record) return '';
    let hasChildRows = false;
    if (record.ChildAvalancheProcessedEvents && record.ChildAvalancheProcessedEvents.length > 0) hasChildRows = true;
    const eventType = eventTypes.find((e) => e.value === record.EventTypeNm);
    const forecastEventType = eventTypes.find((e) => e.value === EventTypes.HazardForecast);
    const isFinishedRoadClosure =
      (record.EventTypeNm === EventTypes.RoadClosure ||
        record.EventTypeNm === EventTypes.AvalancheOccurrence ||
        record.EventTypeNm === EventTypes.AvalancheControl ||
        phasedEventTypes.find((x) => x.value === record.EventTypeNm)) &&
      record.RoadStatus === RoadStatus.Open;
    const isIndependentOccurrence =
      record.EventTypeNm === EventTypes.AvalancheOccurrence && record.ParentEventId == null;
    const isIndependentControl = record.EventTypeNm === EventTypes.AvalancheControl && record.ParentEventId == null;

    const isTransmitted = record.EventTypeNm === EventTypes.HazardForecast && record.Status === 'Transmitted';

    return (
      <>
        <div>
          {(isFinishedRoadClosure ||
            isIndependentControl ||
            isIndependentOccurrence ||
            (record.EventTypeNm === EventTypes.HazardForecast && hasAccess([Permissions.WriteAvalancheHazard]))) &&
            !isTransmitted &&
            actions(record, eventType)}
          {(record.EventTypeNm === EventTypes.RoadClosure ||
            record.EventTypeNm === EventTypes.AvalancheControl ||
            record.EventTypeNm === EventTypes.AvalancheOccurrence ||
            record.EventTypeNm === EventTypes.HazardForecast ||
            phasedEventTypes.find((x) => x.value === record.EventTypeNm)) && (
            <i
              className="fas fa-eye mx-1 my-1 awp-img-btn"
              title="View"
              onClick={() => {
                setMode(FORM_MODE.VIEW);
                setEventId(record.EventId);
                setEventTypeToActOn(eventType);
              }}
            ></i>
          )}
          {(phasedEventTypes.find((x) => x.value === record.EventTypeNm) ||
            record.EventTypeNm === EventTypes.AvalancheOccurrence ||
            record.EventTypeNm === EventTypes.RoadClosure ||
            record.EventTypeNm === EventTypes.AvalancheControl) &&
            EnabledFeature.ProcessedPhasedEventDeleteButton && (
              <EventDeleteButton
                event={record}
                startDate={fromDate}
                setStartDate={setStartDate}
                endDate={toDate}
                setEndDate={setEndDate}
              />
            )}
          {record && (
            <HazardForecastDeleteButton
              event={record}
              startDate={fromDate}
              setStartDate={setStartDate}
              endDate={toDate}
              setEndDate={setEndDate}
            />
          )}
          {(record.EventTypeNm === EventTypes.RoadClosure ||
            record.EventTypeNm === EventTypes.AvalancheControl ||
            record.EventTypeNm === EventTypes.AvalancheOccurrence) && (
            <EventDuplicateButton
              event={record}
              onDuplicate={(clonedEventId) => {
                setEventId(clonedEventId);
                setEventTypeToActOn(eventType);
                setMode(FORM_MODE.EDIT);
              }}
            />
          )}
          {record && (
            <HazardForecastDuplicateButton
              event={record}
              onDuplicate={(clonedEventId) => {
                setEventId(clonedEventId);
                setEventTypeToActOn(forecastEventType);
                setMode(FORM_MODE.EDIT);
                setIsDuplicated(true);
              }}
            />
          )}
          {!isChild && hasChildRows && (
            <>
              {!isValueEmpty(row) &&
                cellComponentExp({
                  row,
                  rows,
                  toggleRowExpanded,
                  isChild,
                  setExpandedRowEntityId,
                  setScrollPosition,
                  EntityId,
                })}
            </>
          )}
        </div>
      </>
    );
  };

  const sortByCustomEventType = (rowA, rowB, id) => {
    const parseEventType = (eventType) => {
      if (!eventType) return null;
      const parts = eventType.split('/');
      return {
        part1: parts[0],
        part2: parts[1],
        part3: parseFloat(parts[2]) || 0,
      };
    };

    const compA = getEventType(rowA.original || rowA, breakpoints);
    const compB = getEventType(rowB.original || rowB, breakpoints);

    if (!compA || !compB) {
      return 'basic';
    }

    const evtA = parseEventType(compA);
    const evtB = parseEventType(compB);

    if (!evtA && evtB) return -1;
    if (evtA && !evtB) return 1;
    if (!evtA && !evtB) return 0;

    if (evtA.part1 !== evtB.part1) {
      return evtA.part1.toLowerCase().localeCompare(evtB.part1.toLowerCase());
    }

    if (evtA.part2 !== evtB.part2) {
      return evtA.part2.toLowerCase().localeCompare(evtB.part2.toLowerCase());
    }
    evtB.part3 = evtB.part3 == '-' ? 0 : evtB.part3;
    evtA.part3 = evtA.part3 == '-' ? 0 : evtA.part3;
    if (evtA.part3 !== evtB.part3) {
      return evtA.part3 >= evtB.part3 ? 1 : -1;
    }

    return 0;
  };

  const renderRowSubComponent = useCallback(
    ({ row, rows, toggleRowExpanded }) => {
      if (!row || !row.original) return '';

      if (!row.original.ChildAvalancheProcessedEvents || row.original.ChildAvalancheProcessedEvents.length === 0)
        return '';

      const sortedColumn = row.cells.find((cell) => cell.column.isSorted);

      if (sortedColumn) {
        const columnId = sortedColumn.column.id;
        const isDesc = sortedColumn.column.isSortedDesc;

        row.original.ChildAvalancheProcessedEvents.sort((a, b) => {
          let comparison = 0;
          switch (columnId) {
            case 'EventTimestamp':
              comparison = new Date(a.EventTimestamp) - new Date(b.EventTimestamp);
              break;
            case 'EventTypeDisp':
              comparison = sortByCustomEventType(a, b, 'EventTypeDisp');
              break;
            case 'EventId':
              comparison = getResizedLocationName(a, breakpoints).localeCompare(getResizedLocationName(b, breakpoints));
              break;
            case 'Status':
              comparison = a.Status.localeCompare(b.Status);
              break;
            case 'AppLastUpdateTimestamp':
              comparison = new Date(a.AppLastUpdateTimestamp) - new Date(b.AppLastUpdateTimestamp);
              break;
            default:
              return 0;
          }
          return isDesc ? -comparison : comparison;
        });
      }

      let isChild = true;

      return (
        <>
          <tr style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
            {breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall ? (
              <td colSpan="7"></td>
            ) : (
              <td colSpan="5"></td>
            )}
          </tr>
          {row.original.ChildAvalancheProcessedEvents.map((evt, i) => {
            let evtTmp = getUtcToLocalByBreakpoints(evt.EventTimestamp, breakpoints);
            let lastTmp = getUtcToLocalByBreakpoints(evt.AppLastUpdateTimestamp, breakpoints);
            let cntlEvtName = getEventType(evt, breakpoints);
            let lv2Location = getResizedLocationName(evt, breakpoints);
            if (
              evt.EventTypeNm === EventTypes.AvalancheControl &&
              evt.ChildAvalancheProcessedEvents &&
              evt.ChildAvalancheProcessedEvents.length > 0
            ) {
              return (
                <React.Fragment key={`frag-rcs-${evt.EventId}-${i}`}>
                  <tr key={`rcs-${evt.EventId}-${i}`} style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
                    <td style={{ width: '15%', borderTop: '0rem' }}>{evtTmp}</td>
                    <td style={{ width: '15%', borderTop: '0rem' }}>
                      <i
                        className="fa fa-arrow-right"
                        aria-hidden="true"
                        style={{ marginRight: '0.2rem', color: '#ffffff' }}
                      />
                      {cntlEvtName}
                    </td>
                    <td colSpan="2" style={{ width: '35%', borderTop: '0rem' }}>
                      {lv2Location}
                    </td>
                    {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                      <td style={{ width: '10%', borderTop: '0rem' }}>{evt.Status}</td>
                    )}
                    {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                      <td style={{ width: '15%', borderTop: '0rem' }}>{lastTmp}</td>
                    )}
                    <td style={{ width: '10%', borderTop: '0rem' }}>
                      {actionMenu(
                        {
                          row: { original: { ...evt, RoadStatus: row.original.RoadStatus } },
                          rows: row.original.ChildAvalancheProcessedEvents.map((x, id) => {
                            return { id, original: x };
                          }),
                          toggleRowExpanded,
                          isChild,
                        },
                        startDate,
                        endDate,
                      )}
                    </td>
                  </tr>
                  {evt.ChildAvalancheProcessedEvents.map((cntl, j) => {
                    let cntlTmp = getUtcToLocalByBreakpoints(cntl.EventTimestamp, breakpoints);
                    let cntllastTmp = getUtcToLocalByBreakpoints(cntl.AppLastUpdateTimestamp, breakpoints);
                    let evtName = getEventType(cntl, breakpoints);
                    let lv3Location = getResizedLocationName(cntl, breakpoints);
                    return (
                      <tr
                        key={`rcs-cntl-${cntl.EventId}-${j}`}
                        style={{ backgroundColor: '#ffffff', color: '#666666' }}
                      >
                        <td style={{ width: '15%', borderTop: '0rem' }}>{cntlTmp}</td>
                        <td style={{ width: '15%', borderTop: '0rem' }}>
                          <i className="fa fa-arrow-right" aria-hidden="true" style={{ marginRight: '0.1rem' }} />
                          <i className="fa fa-arrow-right" aria-hidden="true" style={{ marginRight: '0.2rem' }} />
                          {evtName}
                        </td>
                        <td colSpan="2" style={{ width: '35%', borderTop: '0rem' }}>
                          {lv3Location}
                        </td>
                        {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                          <td style={{ width: '10%', borderTop: '0rem' }}>{cntl.Status}</td>
                        )}
                        {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                          <td style={{ width: '15%', borderTop: '0rem' }}>{cntllastTmp}</td>
                        )}
                        <td style={{ width: '10%', borderTop: '0rem' }}>
                          {actionMenu(
                            {
                              row: { original: { ...cntl, RoadStatus: row.original.RoadStatus } },
                              rows:
                                cntl.ChildAvalancheProcessedEvents?.map((x, id) => {
                                  return { id, original: x };
                                }) ?? [],
                              toggleRowExpanded,
                              isChild,
                            },
                            startDate,
                            endDate,
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            } else {
              return (
                <tr key={`rcs-${evt.EventId}-${i}`} style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
                  <td style={{ width: '15%', borderTop: '0rem' }}>{evtTmp}</td>
                  <td style={{ width: '15%', borderTop: '0rem' }}>
                    {' '}
                    <i
                      className="fa fa-arrow-right"
                      aria-hidden="true"
                      style={{ marginRight: '0.2rem', color: '#ffffff' }}
                    />
                    {cntlEvtName}
                  </td>
                  <td colSpan="2" style={{ width: '35%', borderTop: '0rem' }}>
                    {lv2Location}
                  </td>
                  {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                    <td style={{ width: '10%', borderTop: '0rem' }}>{evt.Status}</td>
                  )}
                  {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                    <td style={{ width: '15%', borderTop: '0rem' }}>{lastTmp}</td>
                  )}
                  <td style={{ width: '10%', borderTop: '0rem' }}>
                    {actionMenu(
                      {
                        row: { original: { ...evt, RoadStatus: row.original.RoadStatus } },
                        rows: row.original.ChildAvalancheProcessedEvents.map((x, id) => {
                          return { id, original: x };
                        }),
                        toggleRowExpanded,
                        isChild,
                      },
                      startDate,
                      endDate,
                    )}
                  </td>
                </tr>
              );
            }
          })}
          <tr style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
            {breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall ? (
              <td colSpan="7" style={{ borderTop: '0rem' }}>
                <Container style={{ justifyContent: 'center', display: 'flex' }}>
                  {cellComponentExp({
                    row,
                    rows,
                    toggleRowExpanded,
                    isChild,
                    setExpandedRowEntityId,
                    setScrollPosition,
                    EntityId,
                  })}
                </Container>
              </td>
            ) : (
              <td colSpan="5" style={{ borderTop: '0rem' }}>
                <Container style={{ justifyContent: 'center', display: 'flex' }}>
                  {cellComponentExp({
                    row,
                    rows,
                    toggleRowExpanded,
                    isChild,
                    setExpandedRowEntityId,
                    setScrollPosition,
                    EntityId,
                  })}
                </Container>
              </td>
            )}
          </tr>
        </>
      );
    },
    [breakpoints, startDate, endDate],
  );
  const getEventType = (row, breakpoints) => {
    if (isValueEmpty(row)) return '';
    const value = row.EventTypeDisp;
    let ret = breakpoints.isLarge ? value : row.Abbreviation;
    if (row.EventTypeNm === EventTypes.AvalancheOccurrence || row.EventTypeNm === EventTypes.AvalancheControl) {
      const size = row.AvalSizeCd == null || row.AvalSizeCd.length == 0 ? '-' : row.AvalSizeCd;
      const trType = row.AvalTrType == null || row.AvalTrType.length == 0 ? '-' : row.AvalTrType;
      ret =
        row.EventTypeNm === EventTypes.AvalancheOccurrence
          ? (breakpoints.isLarge ? 'AvOb' : ret) + '/' + trType + '/' + size
          : ret + '/' + size;
    }
    return ret;
  };

  const getForecastLevel = (row, breakpoints) => {
    if (row.EventTypeDisp !== 'Hazard Forecast' || row.Status !== 'Transmitted') return <></>;
    const level = ForecastLevel[row.HazardForecastLevel ?? 0];
    const levelLabel = breakpoints.isLarge ? level : level.substring(0, 4);
    const className = `forecast-level-box forecast-level-${(level + '')
      .toLowerCase()
      .replace('no hazard', 'nohazard')} px-1`;
    return (
      <div className={className} style={{ whiteSpace: 'nowrap' }}>
        {levelLabel}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        Header:
          !breakpoints.isLarge && !breakpoints.isMedium && !breakpoints.isSmall && !breakpoints.isXLarge
            ? 'Event Time'
            : 'Event Date/Time',
        accessor: 'EventTimestamp',
        sortType: sortByNumericThenAlpha,
        //className:"col-2",
        style: { width: '15%' },
        Cell: ({ value }) => {
          return getUtcToLocalByBreakpoints(value, breakpoints);
        },
      },
      {
        Header: 'Event Type',
        accessor: 'EventTypeDisp',
        style: { width: '15%' },
        sortType: sortByCustomEventType,
        Cell: (props) => {
          if (!props || !props.row || !props.row.original) return '';
          const r = props.row.original;
          return getEventType(r, breakpoints);
        },
      },
      {
        Header: 'Location',
        accessor: 'EventId',
        style: { width: '30%' },
        sortType: sortByNumericThenAlpha,
        Cell: (props) => {
          if (!props || !props.row || !props.row.original) return '';
          return getResizedLocationName(props.row.original, breakpoints);
        },
      },
      {
        Header:
          !breakpoints.isLarge && !breakpoints.isMedium && !breakpoints.isSmall && !breakpoints.isXLarge
            ? 'Level'
            : 'Forecast Level',
        accessor: 'HazardForecastLevel',
        style: { width: '5%' },
        sortType: sortByNumericThenAlpha,
        Cell: (props) => {
          return getForecastLevel(props.row.original, breakpoints);
        },
      },
      {
        Header: 'Status',
        accessor: 'Status',
        style: { width: '9%' },
        sortType: sortByNumericThenAlpha,
        show: breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall,
      },
      {
        Header: breakpoints.isLarge || breakpoints.isMedium ? 'Last Updated' : 'Updated',
        accessor: 'AppLastUpdateTimestamp',
        style: { width: '15%' },
        sortType: sortByNumericThenAlpha,
        show: breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall,
        Cell: ({ value }) => {
          return getUtcToLocalByBreakpoints(value, breakpoints);
        },
      },
      {
        Header: 'Actions',
        accessor: 'id',
        style: { width: '11%', padding: '0px' },
        disableSortBy: true,
        Cell: (props) => actionMenu(props, startDate, endDate),
      },
    ],
    [breakpoints, startDate, endDate],
  );

  return (
    <>
      <div className="d-flex">
        <h1 className="flex-grow-1" style={{ marginBottom: '0px' }}>
          Processed Events List
        </h1>
      </div>
      <Accordion
        defaultActiveKey={isAccordionExpanded ? '0' : '1'}
        onSelect={(event) => {
          setScrollPosition(window.scrollY); // A change in the accordion will change the scroll position
          setIsAccordionExpanded(!!event);
        }}
      >
        <Card border="light" className="page-card">
          <CardHeaderToggle as="h3" eventKey="0" title={title} />
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <Row>
                <Col sm={5} md={6}>
                  <Row>
                    <Col md={6}>
                      <Form.Label className="mr-3" style={{ display: 'inlineBlock', width: '4em' }}>
                        <b>Start</b>
                      </Form.Label>
                      <DatePicker
                        showTime={{ isEnabled: false }}
                        dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        handleChange={(val) => {
                          setStartDate(dateBuilder(new Date(val), startDate, startDateValidator(endDate)));
                          setEndDate(dateBuilder(endDate.value, endDate, endDateValidator(startDate)));
                        }}
                        elementState={startDate}
                      />
                    </Col>
                  </Row>
                </Col>

                <Col sm={5} md={6}>
                  <Row>
                    <Col md={6}>
                      <Form.Label className="mr-3" style={{ display: 'inlineBlock', width: '4em' }}>
                        <b>End</b>
                      </Form.Label>
                      <DatePicker
                        showTime={{ isEnabled: false }}
                        dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        handleChange={(val) => {
                          setEndDate(dateBuilder(new Date(val), endDate, endDateValidator(startDate)));
                          setStartDate(dateBuilder(startDate.value, startDate, startDateValidator(endDate)));
                        }}
                        elementState={endDate}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedProgram}
                    onChange={setSelectedProgram}
                    data={{
                      options: avalanchePrograms,
                      status: 'succeeded',
                    }}
                    title="Program"
                  />
                </Col>
                <Col sm={5} md={6}>
                  <Form.Label>
                    <b>Search</b>
                  </Form.Label>
                  <input
                    className="form-control"
                    placeholder="Search..."
                    id="user_text_search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  ></input>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedArea}
                    onChange={setSelectedArea}
                    data={{
                      options: getFilteredArea(),
                      status: 'succeeded',
                    }}
                    title="Area"
                  />
                </Col>
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedStatus}
                    onChange={setSelectedStatus}
                    data={processedEventStatus}
                    title="Status"
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedEventType}
                    onChange={setSelectedEventType}
                    data={{
                      options: eventTypes,
                      status: 'succeeded',
                    }}
                    title="Event"
                  />
                </Col>
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedAvalanchTech}
                    onChange={setSelectedAvalancheTech}
                    data={avalancheTechs}
                    title="Submitted By"
                  />
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      {fullContextData && (
        <Table
          id="processed_event_table"
          columns={columns}
          data={getFilteredData()}
          fetchData={() => {}}
          loading={loading}
          search={[]}
          filters={[]}
          dataTriggers={[]}
          defaultSortBy="timestamp"
          index={index}
          setIndex={setIndex}
          sort={sort}
          setSort={setSort}
          defaultPageSize={defaultPageSize}
          setDefaultPageSize={setDefaultPageSize}
          renderRowSubComponent={renderRowSubComponent}
          expendRowStyle={{ backgroundColor: '#aaaaaa', color: '#ffffff', padding: '0rem' }}
          trRowStyle={{ backgroundColor: '#444444', color: '#ffffff', padding: '0rem' }}
          thRowStyle={{ padding: '0rem' }}
          treeExpand={true}
          scrollPosition={scrollPosition}
          setScrollPosition={setScrollPosition}
          tableClasses="condensed-rows" //use class to remove top/bottom padding
          simpleBar={false} //toggle table type between simplebar or standard table
          maxHeight={0} //maxHeight set to zero results in full page table
        />
      )}
      <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
    </>
  );
};

ProcessedEventTable.propTypes = {
  title: PropTypes.string,
  breakpoints: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setMode: PropTypes.func,
  eventId: PropTypes.any,
  setEventId: PropTypes.func,
  setEventTypeToActOn: PropTypes.func,
  setEventTypeToCreate: PropTypes.func,
  eventTypeToCreate: PropTypes.object,
  sort: PropTypes.any,
  setSort: PropTypes.func,
  setIsDuplicated: PropTypes.func,
};

export default ProcessedEventTable;
